import { useClipPublishRecords } from '@src/components/content-components/hooks/useClipPublishRecords';
import { usePublishRecordsStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';

import { extractErrorInfo } from '../../utility/error';
import { LogManager } from '../../utility/Logger';
import { VideoVersionList } from './VideoVersionList';

const logger = LogManager.getLogger('component:VideoVersionHistory');

export const VideoVersionHistory: React.FC<{
  sidebarRef: React.RefObject<HTMLDivElement>;
}> = observer(props => {
  const videoCreator = useVideoCreatorStore();
  const publishRecordsStore = usePublishRecordsStore();
  const videoId = videoCreator.currentVideo?.id;
  // Nothing else drops Creator Studio's cache, and the live records reset on
  // story entry — so a publish made on an earlier visit needs a fresh fetch.
  // Declared before the hook so this runs before its fetch.
  useEffect(() => {
    if (videoId) publishRecordsStore.clear(videoId);
  }, [videoId, publishRecordsStore]);
  const { records: publishRecords } = useClipPublishRecords(videoId, null);
  async function switchToVersion(versionId: string) {
    try {
      await videoCreator.loadVideoVersion(versionId);
    } catch (err) {
      logger.error('Failed to load video version', {
        versionId,
        ...extractErrorInfo(err),
      });
      videoCreator.showToast({
        state: 'error',
        message: 'Could not open this version. Please try again.',
      });
    }
  }

  return (
    <VideoVersionList
      versions={videoCreator.currentVideoVersions}
      isLoading={videoCreator.isVersionsHistoryLoading}
      currentVersionId={videoCreator.currentVideo?.versionId}
      onSelect={versionId => void switchToVersion(versionId)}
      page={videoCreator.currentVideoVersionsPage}
      totalPages={videoCreator.currentVideoVersionsTotalPages}
      onPageChange={p =>
        // Paging is a read — must not re-point the loaded version to the
        // head, or returning to page 1 would re-activate the head row.
        videoCreator.loadCurrentVideoVersionsHistory(p, {
          syncLoadedVersionToHead: false,
        })
      }
      sidebarRef={props.sidebarRef}
      publishRecords={publishRecords}
    />
  );
});
